/**
 * Attendance Attempt — the durable record of ONE attendance submission attempt.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-31-1256-feat-attendance-attempt-log-plan.md (U2).
 *
 * This is NOT the parent-absence evidence-of-record row. That row
 * (`PARENT_REPORTED_ABSENCE`) asserts what Blackbaud is verified to hold and is
 * read back to decide which later correction supersedes which earlier report;
 * it deliberately excludes failures. An attempt record is the opposite contract
 * (R6): it never asserts that Blackbaud holds anything — it states only what was
 * attempted and what came back, success, refusal and failure alike. Neither
 * record is derived from or read by the other (R12).
 *
 * An attempt begins when the SUBMITTER COMMITS, not when Blackbaud is called, so
 * a policy refusal that never reaches Blackbaud is a first-class outcome rather
 * than an empty row (R2).
 */

import { z } from 'zod'

/**
 * What became of the attempt as a whole — a CLOSED union, so a reader can say
 * which case it is without parsing prose (R11).
 *
 * `dryRun` is orthogonal to this: a dry run resolves the full write plan and
 * asks Blackbaud for nothing, so it succeeds with `PLANNED` calls (R4, AE3).
 */
export const AttendanceAttemptOutcomeSchema = z.enum([
  /** Every call Blackbaud was asked to accept, it accepted (or there was nothing to write). */
  'SUCCEEDED',
  /** Blackbaud accepted some calls and not others, or some students never resolved. */
  'PARTIALLY_SUCCEEDED',
  /** Policy, authorization or resolution refused the request — no Blackbaud call was made. */
  'REFUSED_BEFORE_BLACKBAUD',
  /** Blackbaud was reached and accepted nothing. */
  'FAILED_AT_BLACKBAUD'
])

export type AttendanceAttemptOutcome = z.infer<typeof AttendanceAttemptOutcomeSchema>

/** Which submission surface the attempt came from (R3). */
export const AttendanceAttemptLaneSchema = z.enum([
  /** A parent reporting from the mobile app. */
  'PARENT',
  /** The school-admin twin of that path, driven by an operator on a parent's behalf. */
  'ADMIN_TOOL',
  /**
   * A teacher saving one section meeting's roster from the app (SCH-1660 U10,
   * R22). Shaped unlike the other two on purpose: a parent report fans out into
   * one Blackbaud call PER STUDENT MEETING, while a teacher save is ONE
   * section-scoped call for the whole roster. So a teacher row carries no
   * per-student `calls` — what Blackbaud answered, the counts, and the records
   * an override destroyed all live on {@link AttendanceAttemptTeacherDetailSchema}.
   */
  'TEACHER'
])

export type AttendanceAttemptLane = z.infer<typeof AttendanceAttemptLaneSchema>

/** What one Blackbaud call did. */
export const AttendanceAttemptCallOutcomeSchema = z.enum([
  /** Blackbaud accepted the call (HTTP 2xx). Read `attachment` for what it then held. */
  'WRITTEN',
  /** Blackbaud refused the call. */
  'FAILED',
  /** A dry run — the call was resolved and deliberately not made. */
  'PLANNED'
])

export type AttendanceAttemptCallOutcome = z.infer<typeof AttendanceAttemptCallOutcomeSchema>

/**
 * What Blackbaud was verified to hold for this meeting AFTER the call — the
 * three-value verdict the write path already computes. Carried for context, not
 * as an assertion the attempt log makes on its own (R6).
 */
export const AttendanceAttemptAttachmentSchema = z.enum(['ATTACHED', 'NOT_ATTACHED', 'UNVERIFIED'])

export type AttendanceAttemptAttachment = z.infer<typeof AttendanceAttemptAttachmentSchema>

/**
 * What Blackbaud said when it refused a call (R5).
 *
 * `responseBody` is present only for REFUSED calls, and never for the
 * authentication variant: Blackbaud's 401/403 bodies can echo user identity,
 * session information and OAuth scope, and the interface layer strips them
 * before even a transient log line. For that variant the taxonomy — type and
 * status code — is recorded and the body is not.
 */
export const AttendanceAttemptBlackbaudErrorSchema = z.object({
  /** The Sky error variant, e.g. `API_ERROR`, `AUTH_ERROR`, `RATE_LIMIT_ERROR`. */
  type: z.string(),
  statusCode: z.number().nullable(),
  /**
   * Blackbaud's own response body, verbatim as the interface layer truncated it.
   * `null` when the variant carries none, or when it is the authentication
   * variant (deliberately withheld — see above).
   */
  responseBody: z.unknown().nullable()
})

export type AttendanceAttemptBlackbaudError = z.infer<typeof AttendanceAttemptBlackbaudErrorSchema>

/** One Blackbaud call the attempt produced — one per meeting (R5). */
export const AttendanceAttemptCallSchema = z.object({
  /** `UsersOnSchools.publicId` of the student the call was about. */
  studentId: z.string(),
  day: z.string(),
  sectionPlatformId: z.number().nullable(),
  meetingStartTime: z.string().nullable(),
  outcome: AttendanceAttemptCallOutcomeSchema,
  attachment: AttendanceAttemptAttachmentSchema,
  /** The one-line failure message, when there was one. */
  error: z.string().nullable(),
  /** Blackbaud's own answer when it refused (R5) — `null` on an accepted call. */
  blackbaud: AttendanceAttemptBlackbaudErrorSchema.nullable()
})

export type AttendanceAttemptCall = z.infer<typeof AttendanceAttemptCallSchema>

/**
 * Why the attempt was refused before any Blackbaud call — the platform's own
 * error type plus the message the submitter was given.
 */
export const AttendanceAttemptRefusalSchema = z.object({
  code: z.string(),
  message: z.string()
})

export type AttendanceAttemptRefusal = z.infer<typeof AttendanceAttemptRefusalSchema>

/** A student whose meetings could not be resolved from Blackbaud. */
export const AttendanceAttemptResolutionErrorSchema = z.object({
  studentId: z.string(),
  error: z.string()
})

export type AttendanceAttemptResolutionError = z.infer<typeof AttendanceAttemptResolutionErrorSchema>

/**
 * ONE record a Present override destroyed (SCH-1660 R22).
 *
 * An explicit Present DELETES whatever exception the office had on that meeting
 * (finding D4) — the one action on this path that removes another author's
 * work. The trail names the student and the reason that was there BEFORE, taken
 * from the pre-write read the override check already made, so an admin can see
 * what went missing without Datadog.
 *
 * The office's comment TEXT is deliberately not here, and never will be: a
 * minor's absence note is written for office staff (R13).
 */
export const AttendanceAttemptTeacherOverrideSchema = z.object({
  /** `UsersOnSchools.publicId` of the student whose record the Present replaced. */
  studentId: z.string(),
  /**
   * The destroyed mark's excuse type (`z_RefAttendanceExcusedTypes.publicId`),
   * or `null` when the school's mirror held no row for the Blackbaud type — the
   * read returns such a mark rather than dropping it, so the override is still
   * recorded, just unnamed.
   */
  priorExcuseTypeId: z.string().nullable()
})

export type AttendanceAttemptTeacherOverride = z.infer<typeof AttendanceAttemptTeacherOverrideSchema>

/**
 * The teacher lane's own facts (SCH-1660 U10 / KTD7).
 *
 * `outcome` here is FINER than the attempt's closed top-level outcome, and both
 * are kept: `succeeded_unverified` is a real 200 from Blackbaud whose read-back
 * did not confirm the meeting was taken (R21), which the top-level union has no
 * way to say and which is the exact state a pilot needs to see.
 *
 * `studentCount` is the ROWS SENT, which under touched-only payloads is the
 * touched count — read it beside `rosterSize`, or an all-present save and a save
 * that lost its marks look identical (KTD7).
 */
export const AttendanceAttemptTeacherDetailSchema = z.object({
  /** Which capability admitted the caller — `teacher` here by construction. */
  capability: z.string(),
  /** `Section.publicId` whose meeting was saved. */
  sectionId: z.string(),
  /** The meeting lookup key the client sent (school-local wall clock). */
  meetingStartTime: z.string(),
  outcome: z.enum(['succeeded', 'succeeded_unverified', 'failed']),
  studentCount: z.number().int().min(0),
  rosterSize: z.number().int().min(0).nullable(),
  overrideCount: z.number().int().min(0).nullable(),
  /**
   * Blackbaud's own message behind a failure, already truncated and stripped of
   * digit runs (KTD7) — Blackbaud echoes student ids into its error text.
   */
  blackbaudMessage: z.string().nullable(),
  overrides: z.array(AttendanceAttemptTeacherOverrideSchema)
})

export type AttendanceAttemptTeacherDetail = z.infer<typeof AttendanceAttemptTeacherDetailSchema>

/** Who acted, and on whose behalf (R4). */
export const AttendanceAttemptActorSchema = z.object({
  /** `UsersOnSchools.publicId` at this school, when the actor has a membership row. */
  schoolUserId: z.string().nullable(),
  /** `User.publicId` — always captured, so a super-admin with no membership is still attributable. */
  userId: z.string().nullable(),
  name: z.string().nullable(),
  /**
   * The actor's Blackbaud user id (`UsersOnSchools.platformId`), resolved at read
   * time. Optional: rows read before the log enriched it carry none, and a
   * super-admin with no membership has no Blackbaud id at this school.
   */
  platformId: z.string().nullable().optional()
})

export type AttendanceAttemptActor = z.infer<typeof AttendanceAttemptActorSchema>

/**
 * A person a page of attempts names, resolved at READ time from
 * `UsersOnSchools` — the stored rows carry only internal public ids, which mean
 * nothing to the operator or the school. The Blackbaud id is what the school can
 * look up on its own side.
 */
export const AttendanceAttemptPersonSchema = z.object({
  name: z.string().nullable(),
  /** Blackbaud user id (`UsersOnSchools.platformId`). */
  platformId: z.string().nullable()
})

export type AttendanceAttemptPerson = z.infer<typeof AttendanceAttemptPersonSchema>

/** A section a page of attempts names, resolved at read time from `Section`. */
export const AttendanceAttemptSectionSchema = z.object({
  name: z.string(),
  /** Blackbaud section id (`Section.platformId`). */
  platformId: z.string().nullable()
})

export type AttendanceAttemptSection = z.infer<typeof AttendanceAttemptSectionSchema>

/**
 * One attendance submission attempt, with its Blackbaud calls nested inside it.
 *
 * The nesting is the point: a submission fans out across meetings, so a flat
 * list of calls cannot answer which part of a report failed.
 */
export const AttendanceAttemptSchema = z
  .object({
    /** `SystemEventLog.publicId` of the attempt row. */
    id: z.string(),
    /**
     * When the attempt's outcome was known, as an ISO-8601 STRING — not a
     * `Date`. This contract crosses `res.json()` and no client revives it.
     */
    occurredAt: z.string(),
    lane: AttendanceAttemptLaneSchema,
    outcome: AttendanceAttemptOutcomeSchema,
    /** True when the submitter asked for a preview and Blackbaud was deliberately not called (AE3). */
    dryRun: z.boolean(),
    /** Present only on `REFUSED_BEFORE_BLACKBAUD`. */
    refusal: AttendanceAttemptRefusalSchema.nullable(),
    /** The person who committed the action — the parent, or the operator on the admin twin. */
    submitter: AttendanceAttemptActorSchema.nullable(),
    /** The parent the record is attributed to. Equal to the submitter on the parent path. */
    attributedParent: AttendanceAttemptActorSchema.nullable(),
    /** `UsersOnSchools.publicId` of every student the submission named. */
    students: z.array(z.string()),
    /** The absence Type requested — `ALL_DAY`, `LATE_ARRIVAL`, `EARLY_DISMISSAL`. */
    requestType: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    /** The chosen reason's public id, and its label when the reason resolved. */
    reasonId: z.string().nullable(),
    reasonLabel: z.string().nullable(),
    /** Students whose meetings could not be read from Blackbaud. */
    resolutionErrors: z.array(AttendanceAttemptResolutionErrorSchema),
    /** One entry per Blackbaud call this attempt produced (R5). */
    calls: z.array(AttendanceAttemptCallSchema),
    /**
     * Present on TEACHER-lane rows only (SCH-1660 U10). `nullish`, not required:
     * every row written before this lane existed parses unchanged.
     */
    teacher: AttendanceAttemptTeacherDetailSchema.nullish()
  })
  .superRefine((attempt, ctx) => {
    // A refusal happened BEFORE Blackbaud, so a refused attempt with calls is a
    // contradiction — and an outcome that names Blackbaud must show the calls it
    // is talking about, or the surface has nothing to expand (R11, AE2).
    if (attempt.outcome === 'REFUSED_BEFORE_BLACKBAUD' && attempt.calls.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['calls'],
        message: 'A refused-before-Blackbaud attempt cannot carry Blackbaud calls'
      })
    }
    // The TEACHER lane is exempt, and only it: its submission is ONE
    // section-scoped Blackbaud call for the whole roster, not one call per
    // student meeting, so there is no per-student `calls` entry to point at.
    // What Blackbaud answered is on `teacher.blackbaudMessage`. Requiring a
    // fabricated call here would put a student id on a row that describes a
    // section-wide failure.
    if (
      attempt.lane !== 'TEACHER' &&
      (attempt.outcome === 'PARTIALLY_SUCCEEDED' || attempt.outcome === 'FAILED_AT_BLACKBAUD') &&
      attempt.calls.length === 0 &&
      attempt.resolutionErrors.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['calls'],
        message: `A ${attempt.outcome} attempt must carry the calls or resolution failures it is describing`
      })
    }
  })

export type AttendanceAttempt = z.infer<typeof AttendanceAttemptSchema>
